import type { AgPromise, ICellRendererComp, ICellRendererParams } from '@ag-grid-community/core';
import { signal } from '@preact/signals-react';
import SideCell from 'src/features/legsHoverCard/SideCell.svelte';
import type { LegsQueryOptions } from 'src/features/legsHoverCard/types';
import { getLegsForOrderQuery } from 'src/queries/legQueries';
import { container } from 'src/StaticContainer';
import { mount, unmount } from 'svelte';

const log = container.get('Logger').getSubLogger({ name: 'OrderSideCellRenderer' });

export interface OrderSideCellRendererOptions {
    /**
     * The field ID in the row data that contains the side value.
     * Used to display the side and detect MULTI orders.
     */
    sideFieldId: string;

    /**
     * The field ID in the row data that contains the clOrdId.
     * Used to fetch legs data from the API for MULTI orders.
     */
    clOrdIdFieldId: string;
}

/**
 * Creates an AG Grid cell renderer for the Order Side client column.
 * Displays the side value and shows an info icon for MULTI orders that triggers the legs hover card.
 */
export const makeCellRenderer = (options: OrderSideCellRendererOptions): { new (): ICellRendererComp } => {
    const { sideFieldId, clOrdIdFieldId } = options;

    return class CellRenderer implements ICellRendererComp {
        #div: HTMLSpanElement = document.createElement('span');
        #root: ReturnType<typeof mount> | undefined;

        #sideValue = signal<string>('');
        #isMultiLeg = signal<boolean>(false);
        #queryOptions = signal<LegsQueryOptions>(null);

        getGui(): HTMLElement {
            this.#div.className = 'flex items-center';
            return this.#div;
        }

        init(params: ICellRendererParams): void | AgPromise<void> {
            this.#updateFromParams(params);

            this.#root = mount(SideCell, {
                target: this.#div,
                props: {
                    sideValue: this.#sideValue,
                    isMultiLeg: this.#isMultiLeg,
                    queryOptions: this.#queryOptions,
                },
            });
        }

        refresh(params: ICellRendererParams): boolean {
            this.#updateFromParams(params);
            return true;
        }

        destroy(): void {
            if (this.#root) {
                unmount(this.#root);
            }
        }

        #updateFromParams(params: ICellRendererParams): void {
            const rowData = params.data;
            const rowId = params.node?.id;

            if (TRACE) {
                log.trace('updateFromParams called', { rowId, rowData, sideFieldId, clOrdIdFieldId });
            }

            if (!rowData || !sideFieldId) {
                this.#sideValue.value = '';
                this.#isMultiLeg.value = false;
                this.#queryOptions.value = null;
                return;
            }

            // Get side value
            const sideValue = rowData[sideFieldId];
            const resolvedSideValue = typeof sideValue === 'string' ? sideValue : String(sideValue ?? '');
            this.#sideValue.value = resolvedSideValue;

            if (TRACE) {
                log.trace('Resolved side value', { rowId, sideValue: resolvedSideValue });
            }

            // Check if multi-leg
            const isMulti = resolvedSideValue.toUpperCase() === 'MULTI';
            this.#isMultiLeg.value = isMulti;

            if (TRACE) {
                log.trace('Checked isMultiLeg', { rowId, isMulti });
            }

            // Update query options for MULTI orders
            if (isMulti && clOrdIdFieldId) {
                const clOrdId = rowData[clOrdIdFieldId];
                if (typeof clOrdId === 'string' && clOrdId) {
                    this.#queryOptions.value = getLegsForOrderQuery(clOrdId);

                    if (TRACE) {
                        log.trace('Updated query options', { rowId, clOrdId });
                    }
                } else {
                    this.#queryOptions.value = null;
                    if (TRACE) {
                        log.trace('No clOrdId found for MULTI order', { rowId });
                    }
                }
            } else {
                this.#queryOptions.value = null;
            }
        }
    };
};
